import { formatPriceWithCurrency } from '@toggle/helpers';
import { EventFeedEvent } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getCardDate } from '~/views/dashboard/whats-new-widget/components/card-base/utils';
import { CardPill } from '~/views/stream/components/stream-card/card-pill/CardPill';
import { CardType } from '~/views/stream/components/stream-card/StreamCard';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '~/widgets/earnings-insight-drawer/useEarningsInsightDrawer';

import * as S from '../../OverviewEventFeed.styles';
import { formatScheduledDate } from './utils';

export interface EarningsRowProps {
  event: EventFeedEvent;
  isLast?: boolean;
  onMarkRead: (eventId: string) => void;
}

export const EarningsRow = ({
  event,
  isLast,
  onMarkRead,
}: EarningsRowProps) => {
  const { t } = useTranslation(['whatsNew', 'earnings', 'calendar']);
  const { openDrawer } = useEarningsInsightDrawer();

  const isPreview = event.metadata.earnings_type === 'preview';
  const epsValue = isPreview
    ? (event.metadata.eps_estimate as number | undefined)
    : (event.metadata.eps_actual as number | undefined);
  const epsDelta = event.metadata.eps_surprise_percentage as number | undefined;
  const currency = event.metadata.currency as string | undefined;
  const reportingDate = event.metadata.reporting_date as string | undefined;
  const isBeat = epsDelta !== undefined && epsDelta > 0;
  const eventDate = new Date(event.created_at);
  const cardType = isPreview
    ? CardType.EarningsPreview
    : CardType.EarningsRecap;

  const handleClick = () => {
    onMarkRead(event.id);
    openDrawer({
      entityTag: event.entity,
      earningsId: event.metadata.earnings_id as string,
      subtab: isPreview ? SubTabsType.Preview : SubTabsType.Recap,
      source: 'asset_overview',
    });
  };

  return (
    <S.FeedItemWrapper $isLast={isLast}>
      <S.FeedItem $isRead={event.read} onClick={handleClick}>
        <S.FeedItemHeader>
          <CardPill type={cardType} isRead={event.read} />
        </S.FeedItemHeader>
        <S.InsightTitle $isRead={event.read}>{event.title}</S.InsightTitle>
        <S.StatsRow>
          <S.StatsContainer>
            {epsValue !== undefined && (
              <S.EarningsLabelWrapper>
                <S.StatLabel>
                  {isPreview
                    ? t('earnings:earningsInsight.drawer.epsExpectations')
                    : t('earnings:earningsInsight.drawer.epsActual')}
                </S.StatLabel>
                <S.PriceValue>
                  {formatPriceWithCurrency(epsValue, currency)}
                </S.PriceValue>
              </S.EarningsLabelWrapper>
            )}
            {isPreview && reportingDate && (
              <>
                <S.VerticalDivider />
                <S.EarningsLabelWrapper>
                  <S.ScheduledLabel>
                    {t('calendar:scheduledFor')}
                  </S.ScheduledLabel>
                  <S.ScheduledDate>
                    {formatScheduledDate(new Date(reportingDate))}
                  </S.ScheduledDate>
                </S.EarningsLabelWrapper>
              </>
            )}
            {!isPreview && epsDelta !== undefined && epsValue !== undefined && (
              <>
                <S.VerticalDivider />
                {isBeat ? (
                  <S.EarningsLabelWrapper>
                    <S.BullishValue>{epsDelta}%</S.BullishValue>
                    <S.BullishText>
                      {t('earnings:earningsInsight.drawer.beat')}
                    </S.BullishText>
                  </S.EarningsLabelWrapper>
                ) : (
                  <S.EarningsLabelWrapper>
                    <S.BearishValue>{Math.abs(epsDelta)}%</S.BearishValue>
                    <S.BearishText>
                      {t('earnings:earningsInsight.drawer.miss')}
                    </S.BearishText>
                  </S.EarningsLabelWrapper>
                )}
              </>
            )}
          </S.StatsContainer>
          <S.TimeAgo>{getCardDate(eventDate, t)}</S.TimeAgo>
        </S.StatsRow>
      </S.FeedItem>
    </S.FeedItemWrapper>
  );
};
